import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { IonTabBar, IonTabButton, IonTabs } from '@ionic/angular/standalone';

import { TranslateModule } from '@ngx-translate/core';

import { type BottomBarTab, BottomBarVisibilityService } from '@core/services/bottom-bar-visibility.service';
import { ThemeService } from '@core/services/theme.service';

import type { BottomBarTabConfig } from '@shared/layouts/bottom-bar/bottom-bar.types';
import { Dynamic } from '@shared/registry/dynamic';

@Component({
  selector: 'app-bottom-bar',
  templateUrl: './bottom-bar.html',
  styleUrl: './bottom-bar.scss',
  imports: [IonTabs, IonTabBar, IonTabButton, TranslateModule, Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomBar {
  private readonly themeService = inject(ThemeService);
  private readonly bottomBarVisibilityService = inject(BottomBarVisibilityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tabBar = viewChild('tabBar', { read: ElementRef });

  protected readonly isTransition = signal(false);
  protected readonly showTabs = this.bottomBarVisibilityService.showTabs;

  constructor() {
    afterNextRender(() => {
      const element = this.tabBar()?.nativeElement ?? document.querySelector('ion-tab-bar');
      if (!element) return;

      const observer = new ResizeObserver((entries) => {
        if (!this.showTabs()) return;
        const height = entries[0]?.borderBoxSize?.[0]?.blockSize ?? element.offsetHeight;
        document.documentElement.style.setProperty('--bottom-bar-height', `${height}px`);
      });
      observer.observe(element);
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        document.documentElement.style.removeProperty('--bottom-bar-height');
      });
    });

    effect(() => {
      if (!this.showTabs()) {
        document.documentElement.style.setProperty('--bottom-bar-height', '0px');
      }
    });
  }

  protected readonly tabs = computed<BottomBarTab[]>(() => {
    const path = this.bottomBarVisibilityService.currentPath();
    const config = this.themeService.scaffold();
    if (!config?.tabs) return [];

    return config.tabs.map((tab: BottomBarTabConfig) => ({
      ...tab,
      isActive: path === `/${tab.route}` || path.startsWith(`/${tab.route}/`),
    }));
  });

  protected onTabChange(): void {
    this.isTransition.set(true);
  }

  protected onTabChanged(): void {
    setTimeout(() => {
      this.isTransition.set(false);
    }, 200);
  }
}
